/**
 * Site survey mode: phone and tablet screens to record an installation on site, room by room,
 * with the board's photo read by the AI, checked against the RGIE as you go, and turned into
 * the single-line diagram and position plans. Everything is a transaction on the open project
 * (undo, cloud sync, offline queue); the project is kept on the device by the App.
 *
 * Navigation lives in the URL hash (#/survey/…), so the phone's back button works.
 */
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { AiFeature } from './AiUpsell.tsx';
import {
  boardCircuits,
  circuitPoints,
  roomPoints,
  surveyBoards,
  surveyRooms,
  type History,
  type Id,
  type Op,
  type Project,
  type ProjectMeta,
  type SurveyBoard,
  type SurveyCircuit,
  type SurveyPoint,
  type SurveyRoom,
  localeTag,
} from '@elec/domain';
import { extractionToBoard, type AiService, type BoardExtraction, isAiUnavailable } from '@elec/ai';
import type { Locale, MessageKey, Translate } from '@elec/i18n';
import { listLibrary, type LibraryEntry } from '@elec/platform';
import type { ProjectSummary } from '@elec/sync';
import {
  addBoardOp,
  addCircuitOp,
  addPointOp,
  addRoomOp,
  applyBoardSpecOps,
  generateFromSurvey,
  LABELS,
  earthResistanceOps,
  moveOps,
  nextRcdKey,
  POINT_PRESETS,
  presetOf,
  removeBoardOps,
  removeCircuitOps,
  removeRcdOps,
  removeRoomOps,
  surveyCodes,
  surveyFindings,
  surveyPreview,
  surveySheets,
  surveyStale,
  type PointPreset,
  type SurveyFinding,
} from '@elec/survey';
import { ROOM_TYPES, getSymbol } from '@elec/symbols';
import type { AssetStore } from './assets.ts';
import type { CloudManager, CloudState } from './cloud.ts';
import { pickPhotos, preparePhoto } from './photos.ts';
import { analyse } from '@elec/analysis';
import { inspectionsOf, RemarkCard, remarksFor } from './Inspections.tsx';
import { measurementProgress, MeasurementsEditor } from './Measurements.tsx';
import { Chips, NumberField, PhotoThumb, PointIcon, Sheet, Stepper, TextField } from './surveyUi.tsx';
import './survey.css';

// ---------- install prompt (Android / desktop Chrome) ----------
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
}
let installPrompt: InstallPrompt | null = null;
const installListeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installPrompt = e as InstallPrompt;
    installListeners.forEach((f) => f());
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    installListeners.forEach((f) => f());
  });
}
const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);
const isIos = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

// ---------- routes ----------
type Tab = 'rooms' | 'board' | 'checks' | 'info';
export type SurveyRoute =
  { screen: 'home' } | { screen: 'project'; tab: Tab; room?: Id; board?: Id; circuit?: Id };

export function parseSurveyHash(hash: string): SurveyRoute | null {
  const m = /^#\/survey(?:\/(.*))?$/.exec(hash);
  if (!m) return null;
  const parts = (m[1] ?? '').split('/').filter(Boolean);
  if (parts[0] !== 'p') return { screen: 'home' };
  const [, a, b] = parts;
  if (a === 'room' && b) return { screen: 'project', tab: 'rooms', room: b };
  if (a === 'circuit' && b) return { screen: 'project', tab: 'board', circuit: b };
  if (a === 'board') return { screen: 'project', tab: 'board', ...(b ? { board: b } : {}) };
  if (a === 'checks' || a === 'info' || a === 'rooms') return { screen: 'project', tab: a };
  return { screen: 'project', tab: 'rooms' };
}
export function surveyHash(r: SurveyRoute): string {
  if (r.screen === 'home') return '#/survey';
  if (r.room) return `#/survey/p/room/${r.room}`;
  if (r.circuit) return `#/survey/p/circuit/${r.circuit}`;
  if (r.tab === 'board') return r.board ? `#/survey/p/board/${r.board}` : '#/survey/p/board';
  return `#/survey/p/${r.tab}`;
}

function useMedia(q: string) {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(q);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(q).matches,
  );
}

export interface SurveyStatus {
  label: string;
  tone: 'ok' | 'warn' | 'muted';
}

export interface SurveyProps {
  route: SurveyRoute;
  go(r: SurveyRoute, replace?: boolean): void;
  project: Project;
  history: History;
  assets: AssetStore;
  t: Translate;
  locale: Locale;
  setLocale(l: Locale): void;
  ai: AiService;
  /** Runs an AI feature when signed in; otherwise asks to sign in first. */
  withAccount(feature: AiFeature, run: () => void): Promise<void>;
  cloud: CloudManager;
  cloudState: CloudState;
  ask(r: { title: string; body: string; confirm: string; danger?: boolean }): Promise<boolean>;
  notify(msg: string): void;
  status: SurveyStatus;
  /** The open project is kept on this device or in the cloud. */
  stored: boolean;
  /** Key of the open project in the device library, or of its cloud copy. */
  openKey: string | null;
  newSurvey(): Promise<void>;
  openEntry(e: LibraryEntry): Promise<void>;
  openCloud(id: string): Promise<void>;
  removeEntry(e: LibraryEntry): Promise<void>;
  keepOnDevice(): Promise<void>;
  openEditor(sheetId?: Id): void;
  exportPdf(): void;
  signIn(): void;
  /** Upload the open project to the current team (signed in, not in the cloud yet). */
  upload(): Promise<void>;
}

const ICONS: Record<Tab | 'back' | 'undo' | 'camera' | 'image' | 'trash' | 'up' | 'down' | 'chev', string> = {
  rooms: 'M3 4 H17 V16 H3 Z M3 10 H9 M12 4 V8',
  board: 'M4 3 H16 V17 H4 Z M7 6 V10 M10 6 V10 M13 6 V10 M6 13 H14',
  checks: 'M4 10 L8 14 L16 5',
  info: 'M10 9 V14 M10 6 h0.01 M10 2 A8 8 0 1 0 10.01 2',
  back: 'M12 4 L6 10 L12 16',
  undo: 'M7 5 L3 9 L7 13 M3 9 H12 A5 5 0 0 1 12 19 H9',
  camera: 'M3 7 H6 L8 4 H12 L14 7 H17 V16 H3 Z M10 9 A3 3 0 1 0 10.01 9',
  image: 'M3 4 H17 V16 H3 Z M3 13 L8 9 L12 13 L14 11 L17 14 M13 7 h0.01',
  trash: 'M4 6 H16 M8 6 V4 H12 V6 M6 6 L7 17 H13 L14 6',
  up: 'M5 12 L10 7 L15 12',
  down: 'M5 8 L10 13 L15 8',
  chev: 'M8 5 L13 10 L8 15',
};
const Ico = ({ name, size = 20 }: { name: keyof typeof ICONS; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={ICONS[name]} />
  </svg>
);

/** The survey's language button cycles FR → NL → EN. */
const NEXT_LOCALE: Record<Locale, Locale> = { fr: 'nl', nl: 'en', en: 'fr' };
const roomTypeLabel = (v: string, locale: Locale) =>
  ROOM_TYPES.find((r) => r.value === v)?.label[locale] ?? v;
const WET = new Set(ROOM_TYPES.filter((r) => r.wet).map((r) => r.value));
/** Appliances RGIE wants behind a 30 mA RCD wherever they are. */
const WET_APPLIANCES = new Set(['iec.washing-machine', 'iec.dryer', 'iec.dishwasher']);
const fmt = (n: number) => String(n).replace('.', ',');

/** Sensible defaults for a new circuit feeding this kind of point (checked by the rules). */
function circuitDefaults(symbolId: string): Partial<SurveyCircuit> {
  const kind = getSymbol(symbolId)?.category;
  if (kind === 'lighting' || kind === 'command')
    return { ratingA: 16, curve: 'B', cable: { type: 'XVB', sectionMm2: 1.5, cores: 3 } };
  if (symbolId === 'iec.cooker')
    return { ratingA: 32, curve: 'B', cable: { type: 'XVB', sectionMm2: 6, cores: 3 } };
  return { ratingA: 20, curve: 'B', cable: { type: 'XVB', sectionMm2: 2.5, cores: 3 } };
}

export function Survey(props: SurveyProps) {
  const { route, t } = props;
  const wide = useMedia('(min-width: 900px)');
  if (route.screen === 'home') return <Home {...props} />;
  return <ProjectScreen {...props} route={route} wide={wide} t={t} />;
}

// ---------- home ----------

function Home(props: SurveyProps) {
  const { t, cloudState } = props;
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [remote, setRemote] = useState<ProjectSummary[]>([]);
  const [, setTick] = useState(0);
  useEffect(() => {
    const f = () => setTick((x) => x + 1);
    installListeners.add(f);
    return () => void installListeners.delete(f);
  }, []);
  const refresh = () => void listLibrary().then(setEntries);
  useEffect(refresh, []);
  useEffect(() => {
    const team = cloudState.team;
    if (!team || !props.cloud.client || cloudState.offline) return setRemote([]);
    void props.cloud.client
      .projects(team.id)
      .then(setRemote)
      .catch(() => setRemote([]));
  }, [cloudState.team?.id, cloudState.offline]);
  const cloudCopies = new Set((entries ?? []).filter((e) => e.cloud).map((e) => e.cloud!.projectId));
  const current = props.project;
  const showCurrent = !props.stored && (current.meta.title || Object.keys(current.surveyRooms).length > 0);
  const standalone = isStandalone();

  return (
    <div className="survey sv-home">
      <header className="sv-bar">
        <img src="./logo.svg" alt="" width={26} height={26} />
        <h1>{t('survey.homeTitle')}</h1>
        <div className="sv-bar-end">
          <button
            className="sv-link"
            onClick={() => props.setLocale(NEXT_LOCALE[props.locale])}
            data-testid="sv-lang"
            aria-label={t('menu.language')}
          >
            {props.locale.toUpperCase()}
          </button>
          <button className="sv-link" onClick={() => props.openEditor()} data-testid="sv-editor">
            {t('survey.openEditor')}
          </button>
        </div>
      </header>
      <main className="sv-scroll">
        <p className="sv-intro">{t('survey.intro')}</p>
        <button className="sv-primary sv-block" onClick={() => void props.newSurvey()} data-testid="sv-new">
          + {t('survey.new')}
        </button>
        {!standalone && (installPrompt || isIos()) && (
          <div className="sv-note">
            {installPrompt ? (
              <button className="sv-secondary" onClick={() => void installPrompt?.prompt()}>
                {t('survey.install')}
              </button>
            ) : (
              <span>{t('survey.installIos')}</span>
            )}
            <span className="muted">{t('survey.storageWarn')}</span>
          </div>
        )}
        {showCurrent && (
          <section>
            <h2>{t('survey.current')}</h2>
            <div className="sv-card">
              <button
                className="sv-row"
                onClick={() => props.go({ screen: 'project', tab: 'rooms' })}
                data-testid="sv-current"
              >
                <span className="sv-row-main">
                  <strong>{current.meta.title || '—'}</strong>
                  <small>{props.status.label}</small>
                </span>
                <Ico name="chev" />
              </button>
              <button
                className="sv-row sv-row-action"
                onClick={() => void props.keepOnDevice().then(refresh)}
              >
                {t('survey.keepOnDevice')}
              </button>
            </div>
          </section>
        )}
        <section>
          <h2>{t('survey.onDevice')}</h2>
          {entries && entries.length === 0 && <p className="muted">{t('survey.noProjects')}</p>}
          {entries && entries.length > 0 && (
            <div className="sv-card" data-testid="sv-library">
              {entries.map((e) => (
                <div className="sv-row-wrap" key={e.key}>
                  <button className="sv-row" onClick={() => void props.openEntry(e)} data-key={e.key}>
                    <span className="sv-row-main">
                      <strong>
                        {e.title || '—'} {e.key === props.openKey && <span className="sv-badge">●</span>}
                      </strong>
                      <small>
                        {e.cloud ? `${t('survey.cloudBadge')} · ${t('survey.offlineCopy')} · ` : ''}
                        {new Date(e.updatedAt).toLocaleString(localeTag(props.locale), {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                        {' · '}
                        {t('survey.pointCount', {
                          n: Object.values(e.project.surveyPoints ?? {}).reduce((n, p) => n + p.quantity, 0),
                        })}
                      </small>
                    </span>
                    <Ico name="chev" />
                  </button>
                  <button
                    className="sv-icon-btn"
                    aria-label={t('survey.remove')}
                    title={t('survey.remove')}
                    onClick={async () => {
                      if (
                        await props.ask({
                          title: t('survey.remove'),
                          body: t('survey.removeConfirm', { name: e.title || '—' }),
                          confirm: t('survey.remove'),
                          danger: true,
                        })
                      ) {
                        await props.removeEntry(e);
                        refresh();
                      }
                    }}
                  >
                    <Ico name="trash" size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
        {cloudState.me && cloudState.team && remote.filter((r) => !cloudCopies.has(r.id)).length > 0 && (
          <section>
            <h2>{t('survey.cloudTeam', { team: cloudState.team.name })}</h2>
            <div className="sv-card">
              {remote
                .filter((r) => !cloudCopies.has(r.id))
                .map((r) => (
                  <button className="sv-row" key={r.id} onClick={() => void props.openCloud(r.id)}>
                    <span className="sv-row-main">
                      <strong>{r.title || '—'}</strong>
                    </span>
                    <Ico name="chev" />
                  </button>
                ))}
            </div>
          </section>
        )}
        {!cloudState.me && props.cloud.client && (
          <button className="sv-secondary sv-block" onClick={props.signIn}>
            {t('survey.signIn')}
          </button>
        )}
      </main>
    </div>
  );
}

// ---------- project ----------

interface Ctx extends SurveyProps {
  dispatch(label: string, ops: Op[]): void;
  codes: Map<Id, string>;
  readOnly: boolean;
}

function ProjectScreen(
  props: SurveyProps & { route: Extract<SurveyRoute, { screen: 'project' }>; wide: boolean },
) {
  const { project, history, t, route, wide, go } = props;
  const codes = useMemo(() => surveyCodes(project), [project]);
  const readOnly = history.readOnly;
  const ctx: Ctx = {
    ...props,
    codes,
    readOnly,
    dispatch: (label, ops) => {
      if (ops.length && !readOnly) history.dispatch({ label, ops });
    },
  };
  // A route to something deleted (by undo, or by someone else) falls back to its list.
  const room = route.room ? project.surveyRooms[route.room] : undefined;
  const circuit = route.circuit ? project.surveyCircuits[route.circuit] : undefined;
  useEffect(() => {
    if (route.room && !room) go({ screen: 'project', tab: 'rooms' }, true);
    if (route.circuit && !circuit) go({ screen: 'project', tab: 'board' }, true);
  }, [route.room, route.circuit, room, circuit]);

  const detail = room ? (
    <RoomView ctx={ctx} room={room} />
  ) : circuit ? (
    <CircuitView ctx={ctx} circuit={circuit} />
  ) : null;
  const main =
    route.tab === 'rooms' ? (
      <RoomsList ctx={ctx} />
    ) : route.tab === 'board' ? (
      <BoardView ctx={ctx} boardId={route.board ?? circuit?.boardId} />
    ) : route.tab === 'checks' ? (
      <ChecksView ctx={ctx} />
    ) : (
      <InfoView ctx={ctx} />
    );
  const backTo: SurveyRoute = detail
    ? { screen: 'project', tab: route.tab, ...(circuit ? { board: circuit.boardId } : {}) }
    : { screen: 'home' };
  const title = room
    ? room.name || t('survey.roomName')
    : circuit
      ? circuitTitle(ctx, circuit)
      : project.meta.title || t('survey.mode');
  const tabs: Array<{ id: Tab; label: MessageKey }> = [
    { id: 'rooms', label: 'survey.tabRooms' },
    { id: 'board', label: 'survey.tabBoard' },
    { id: 'checks', label: 'survey.tabChecks' },
    { id: 'info', label: 'survey.tabInfo' },
  ];
  const nWarn = useMemo(
    () =>
      route.tab === 'checks'
        ? 0
        : surveyFindings(project).filter((f) => f.severity !== 'info' && !f.fromDiagram).length,
    [project, route.tab],
  );
  const tabBar = (
    <nav className="sv-tabs" aria-label={t('survey.mode')}>
      {tabs.map((x) => (
        <button
          key={x.id}
          className={route.tab === x.id ? 'on' : ''}
          aria-current={route.tab === x.id ? 'page' : undefined}
          onClick={() => go({ screen: 'project', tab: x.id }, route.tab === x.id || !!detail)}
          data-testid={`sv-tab-${x.id}`}
        >
          <Ico name={x.id} size={22} />
          <span>{t(x.label)}</span>
          {x.id === 'checks' && nWarn > 0 && <i className="sv-dot">{nWarn}</i>}
        </button>
      ))}
    </nav>
  );
  return (
    <div className={`survey sv-project${wide ? ' wide' : ''}`}>
      <header className="sv-bar">
        <button
          className="sv-icon-btn"
          aria-label={t('survey.back')}
          onClick={() => go(wide && detail ? { screen: 'home' } : backTo)}
          data-testid="sv-back"
        >
          <Ico name="back" size={22} />
        </button>
        <h1 title={title}>{wide ? project.meta.title || t('survey.mode') : title}</h1>
        <span className={`sv-status ${props.status.tone}`} data-testid="sv-status">
          {props.status.label}
        </span>
        <button
          className="sv-icon-btn"
          aria-label={t('menu.undo')}
          title={history.undoLabel ? `${t('menu.undo')} : ${history.undoLabel}` : t('menu.undo')}
          disabled={!history.canUndo || readOnly}
          onClick={() => history.undo()}
          data-testid="sv-undo"
        >
          <Ico name="undo" />
        </button>
      </header>
      {wide && tabBar}
      {wide ? (
        <div className="sv-panes">
          <main className="sv-scroll sv-pane-main">{main}</main>
          <section className="sv-scroll sv-pane-detail">
            {detail ?? <p className="muted sv-pad">{t('survey.noRooms')}</p>}
          </section>
        </div>
      ) : (
        <main className="sv-scroll">{detail ?? main}</main>
      )}
      {!wide && tabBar}
    </div>
  );
}

const circuitTitle = (ctx: Ctx, c: SurveyCircuit) =>
  `${ctx.t('survey.circuit')} ${ctx.codes.get(c.id) ?? ''}${c.label ? ` · ${c.label}` : ''}`;

function Section(props: { title?: string; children: ReactNode; end?: ReactNode }) {
  return (
    <section className="sv-section">
      {(props.title || props.end) && (
        <div className="sv-section-head">
          {props.title && <h2>{props.title}</h2>}
          {props.end}
        </div>
      )}
      {props.children}
    </section>
  );
}

// ---------- rooms ----------

function RoomsList({ ctx }: { ctx: Ctx }) {
  const { project, t, go, locale } = ctx;
  const rooms = surveyRooms(project);
  const levels = [...new Set(rooms.map((r) => r.level.trim()))];
  const add = () => {
    const op = addRoomOp(project, { name: '', roomType: 'other' });
    ctx.dispatch(t('survey.addRoom'), [op]);
    go({ screen: 'project', tab: 'rooms', room: op.value.id });
  };
  return (
    <div className="sv-pad">
      {rooms.length === 0 && <p className="sv-empty">{t('survey.noRooms')}</p>}
      {levels.map((level) => (
        <Section
          key={level || '-'}
          title={level || (levels.length > 1 ? LABELS[locale].defaultLevel : undefined)}
        >
          <div className="sv-card" data-testid="sv-rooms">
            {rooms
              .filter((r) => r.level.trim() === level)
              .map((r) => {
                const pts = roomPoints(project, r.id);
                const n = pts.reduce((s, p) => s + p.quantity, 0);
                const free = pts.filter((p) => !p.circuitId).reduce((s, p) => s + p.quantity, 0);
                return (
                  <button
                    key={r.id}
                    className={`sv-row${ctx.route.screen === 'project' && ctx.route.room === r.id ? ' on' : ''}`}
                    onClick={() => go({ screen: 'project', tab: 'rooms', room: r.id })}
                    data-room={r.name}
                  >
                    <span className="sv-row-main">
                      <strong>{r.name || '—'}</strong>
                      <small>
                        {roomTypeLabel(r.roomType, locale)} · {t('survey.pointCount', { n })}
                        {free > 0 && <em className="sv-warn"> · {t('survey.unassigned', { n: free })}</em>}
                      </small>
                    </span>
                    <span className="sv-mini-icons">
                      {[...new Set(pts.map((p) => p.symbolId))].slice(0, 4).map((s) => (
                        <PointIcon key={s} symbolId={s} size={18} />
                      ))}
                    </span>
                    <Ico name="chev" />
                  </button>
                );
              })}
          </div>
        </Section>
      ))}
      {!ctx.readOnly && (
        <button className="sv-primary sv-block" onClick={add} data-testid="sv-add-room">
          + {t('survey.addRoom')}
        </button>
      )}
    </div>
  );
}

function RoomView({ ctx, room }: { ctx: Ctx; room: SurveyRoom }) {
  const { project, t, locale } = ctx;
  const pts = roomPoints(project, room.id);
  const [picker, setPicker] = useState<SurveyPoint | null>(null);
  const [more, setMore] = useState(false);
  /** Point whose photos are open. */
  const [photosOf, setPhotosOf] = useState<Id | null>(null);
  const photoPoint = photosOf ? project.surveyPoints[photosOf] : undefined;
  const levels = [
    ...new Set(
      surveyRooms(project)
        .map((r) => r.level)
        .filter(Boolean),
    ),
  ];
  const patch = (p: Partial<SurveyRoom>) =>
    ctx.dispatch(t('tx.edit'), [{ op: 'update', coll: 'surveyRooms', id: room.id, patch: p }]);
  const addPoint = (preset: PointPreset) => {
    // Same kind of point as one already in the room: same circuit, most likely.
    const like = [...pts].reverse().find((p) => p.symbolId === preset.symbolId && p.circuitId);
    ctx.dispatch(t('tx.add', { name: preset.label[locale] }), [
      addPointOp(project, room.id, preset, like?.circuitId ? { circuitId: like.circuitId } : {}),
    ]);
    setMore(false);
  };
  const common = POINT_PRESETS.filter((p) => p.common);
  return (
    <div className="sv-pad" data-testid="sv-room">
      <div className="sv-form">
        <TextField
          label={t('survey.roomName')}
          value={room.name}
          onCommit={(name) => patch({ name })}
          testId="sv-room-name"
          autoFocus={!room.name}
        />
        <label className="sv-field">
          <span>{t('survey.roomType')}</span>
          <select
            value={room.roomType}
            onChange={(e) => patch({ roomType: e.target.value })}
            data-testid="sv-room-type"
          >
            {ROOM_TYPES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label[locale]}
              </option>
            ))}
          </select>
        </label>
        <TextField
          label={t('survey.level')}
          value={room.level}
          placeholder={LABELS[locale].defaultLevel}
          list="sv-levels"
          onCommit={(level) => patch({ level })}
          testId="sv-room-level"
        />
        <datalist id="sv-levels">
          {levels.map((l) => (
            <option key={l} value={l} />
          ))}
        </datalist>
      </div>

      <Section title={t('survey.points')}>
        {pts.length === 0 && <p className="muted">{t('survey.noPoints')}</p>}
        {pts.length > 0 && (
          <div className="sv-card" data-testid="sv-points">
            {pts.map((p) => {
              const preset = presetOf(p);
              const c = p.circuitId ? project.surveyCircuits[p.circuitId] : undefined;
              return (
                <div className="sv-point" key={p.id} data-point={preset?.key}>
                  <PointIcon symbolId={p.symbolId} />
                  <span className="sv-point-name">
                    {preset?.label[locale] ?? getSymbol(p.symbolId)?.name[locale]}
                  </span>
                  <Stepper
                    label={t('survey.quantity')}
                    value={p.quantity}
                    onChange={(quantity) =>
                      ctx.dispatch(t('tx.edit'), [
                        { op: 'update', coll: 'surveyPoints', id: p.id, patch: { quantity } },
                      ])
                    }
                  />
                  <button
                    className={`sv-circuit-chip${c ? '' : ' none'}`}
                    onClick={() => setPicker(p)}
                    data-testid="sv-point-circuit"
                  >
                    {c
                      ? `${ctx.codes.get(c.id) ?? '?'}${c.label ? ` · ${c.label}` : ''}`
                      : t('survey.noCircuit')}
                  </button>
                  <span className="sv-point-actions">
                    <button
                      className="sv-icon-btn sv-photo-btn"
                      aria-label={t('survey.pointPhotos', { n: p.photos?.length ?? 0 })}
                      onClick={() => setPhotosOf(p.id)}
                      data-testid="sv-point-photos"
                    >
                      <Ico name="camera" size={17} />
                      {!!p.photos?.length && <i className="sv-dot">{p.photos.length}</i>}
                    </button>
                    {!ctx.readOnly && (
                      <button
                        className="sv-icon-btn"
                        aria-label={t('survey.deletePoint')}
                        onClick={() =>
                          ctx.dispatch(t('tx.delete'), [{ op: 'remove', coll: 'surveyPoints', id: p.id }])
                        }
                      >
                        <Ico name="trash" size={17} />
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {!ctx.readOnly && (
          <div className="sv-quick" data-testid="sv-quick">
            {common.map((p) => (
              <button key={p.key} onClick={() => addPoint(p)} data-preset={p.key}>
                <PointIcon symbolId={p.symbolId} size={24} />
                <span>{p.label[locale]}</span>
              </button>
            ))}
            <button onClick={() => setMore(true)} data-testid="sv-more-points">
              <span className="sv-plus">+</span>
              <span>{t('survey.morePoints')}</span>
            </button>
          </div>
        )}
      </Section>

      <Photos
        ctx={ctx}
        title={t('survey.photos')}
        photos={room.photos}
        onChange={(photos) => patch({ photos })}
      />

      <Section>
        <TextField
          label={t('survey.notes')}
          value={room.notes}
          multiline
          onCommit={(notes) => patch({ notes })}
        />
      </Section>

      {!ctx.readOnly && (
        <button
          className="sv-danger sv-block"
          onClick={async () => {
            if (
              await ctx.ask({
                title: t('survey.deleteRoom'),
                body: t('survey.deleteRoomConfirm', { name: room.name || '—' }),
                confirm: t('survey.deleteRoom'),
                danger: true,
              })
            ) {
              ctx.go({ screen: 'project', tab: 'rooms' }, true);
              ctx.dispatch(t('survey.deleteRoom'), removeRoomOps(project, room.id));
            }
          }}
        >
          {t('survey.deleteRoom')}
        </button>
      )}

      {more && (
        <Sheet title={t('survey.morePoints')} onClose={() => setMore(false)} testId="sv-more-sheet">
          <div className="sv-quick sv-quick-all">
            {POINT_PRESETS.map((p) => (
              <button key={p.key} onClick={() => addPoint(p)} data-preset={p.key}>
                <PointIcon symbolId={p.symbolId} size={24} />
                <span>{p.label[locale]}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}
      {picker && <CircuitPicker ctx={ctx} room={room} point={picker} onClose={() => setPicker(null)} />}
      {photoPoint && (
        <Sheet
          title={`${presetOf(photoPoint)?.label[locale] ?? getSymbol(photoPoint.symbolId)?.name[locale] ?? ''} — ${room.name || '—'}`}
          onClose={() => setPhotosOf(null)}
          testId="sv-point-photo-sheet"
        >
          <Photos
            ctx={ctx}
            title={t('survey.photos')}
            photos={photoPoint.photos ?? []}
            onChange={(photos) =>
              ctx.dispatch(t('tx.edit'), [
                {
                  op: 'update',
                  coll: 'surveyPoints',
                  id: photoPoint.id,
                  patch: { photos: photos.length ? photos : undefined },
                },
              ])
            }
          />
        </Sheet>
      )}
    </div>
  );
}

function CircuitPicker({
  ctx,
  room,
  point,
  onClose,
}: {
  ctx: Ctx;
  room: SurveyRoom;
  point: SurveyPoint;
  onClose(): void;
}) {
  const { project, t, locale } = ctx;
  const boards = surveyBoards(project);
  const assign = (circuitId: Id | undefined) => {
    ctx.dispatch(t('tx.edit'), [{ op: 'update', coll: 'surveyPoints', id: point.id, patch: { circuitId } }]);
    onClose();
  };
  const create = () => {
    const ops: Op[] = [];
    let board = boards[0];
    if (!board) {
      const b = addBoardOp(project, { name: t('survey.mainBoard') });
      ops.push(b);
      board = b.value;
    }
    const preset = presetOf(point);
    const wet = WET.has(room.roomType) || WET_APPLIANCES.has(point.symbolId);
    const rcd30 = board.rcds.find((r) => r.sensitivityMa <= 30);
    const c = addCircuitOp(project, board.id, {
      label: `${preset?.label[locale] ?? ''}${room.name ? ` ${room.name}` : ''}`.trim(),
      ...circuitDefaults(point.symbolId),
      ...(wet && rcd30 ? { rcdKey: rcd30.key } : {}),
    });
    ops.push(c, { op: 'update', coll: 'surveyPoints', id: point.id, patch: { circuitId: c.value.id } });
    ctx.dispatch(t('survey.newCircuit'), ops);
    onClose();
  };
  return (
    <Sheet title={t('survey.chooseCircuit')} onClose={onClose} testId="sv-circuit-picker">
      <div className="sv-card">
        {boards.map((b) =>
          boardCircuits(project, b.id).map((c) => (
            <button
              key={c.id}
              className={`sv-row${point.circuitId === c.id ? ' on' : ''}`}
              onClick={() => assign(c.id)}
              data-code={ctx.codes.get(c.id)}
            >
              <span className="sv-code">{ctx.codes.get(c.id)}</span>
              <span className="sv-row-main">
                <strong>{c.label || '—'}</strong>
                <small>
                  {circuitSpecs(ctx, c)}
                  {boards.length > 1 ? ` · ${b.name}` : ''}
                </small>
              </span>
            </button>
          )),
        )}
        <button className={`sv-row${point.circuitId ? '' : ' on'}`} onClick={() => assign(undefined)}>
          <span className="sv-code">–</span>
          <span className="sv-row-main">
            <strong>{t('survey.noCircuit')}</strong>
          </span>
        </button>
      </div>
      {!ctx.readOnly && (
        <button className="sv-primary sv-block" onClick={create} data-testid="sv-new-circuit">
          + {t('survey.newCircuit')}
        </button>
      )}
    </Sheet>
  );
}

const circuitSpecs = (ctx: Ctx, c: SurveyCircuit) => {
  const dev = { MCB: '', RCBO: `${ctx.t('survey.devRCBO')} `, fuse: `${ctx.t('survey.devFuse')} ` }[c.device];
  const parts = [
    c.ratingA ? `${dev}${c.curve && c.device !== 'fuse' ? c.curve : ''}${c.ratingA} A` : dev.trim(),
    c.cable?.sectionMm2 ? `${c.cable.cores}G${fmt(c.cable.sectionMm2)}` : '',
    ctx.t('survey.pointCount', { n: circuitPoints(ctx.project, c.id).reduce((n, p) => n + p.quantity, 0) }),
  ];
  return parts.filter(Boolean).join(' · ');
};

// ---------- photos ----------

function Photos(props: {
  ctx: Ctx;
  title: string;
  photos: string[];
  onChange(photos: string[]): void;
  extra?: ReactNode;
}) {
  const { ctx } = props;
  const { t } = ctx;
  const [big, setBig] = useState<string | null>(null);
  const add = async (camera: boolean) => {
    const files = await pickPhotos(camera);
    if (!files.length) return;
    const ids: string[] = [];
    for (const f of files) {
      try {
        const ph = await preparePhoto(f);
        await ctx.assets.add(ph.id, ph.bytes, ph.mime, false);
        ids.push(ph.id);
      } catch (e) {
        ctx.notify((e as Error).message);
      }
    }
    if (ids.length) props.onChange([...props.photos, ...ids]);
  };
  return (
    <Section title={props.title} end={props.extra}>
      <div className="sv-photos" data-testid="sv-photos">
        {props.photos.map((id) => (
          <PhotoThumb
            key={id}
            bytes={ctx.assets.bytes.get(id)}
            alt={props.title}
            onClick={() => setBig(id)}
          />
        ))}
        {!ctx.readOnly && (
          <>
            <button className="sv-photo-add" onClick={() => void add(true)} data-testid="sv-take-photo">
              <Ico name="camera" size={24} />
              <span>{t('survey.takePhoto')}</span>
            </button>
            <button className="sv-photo-add" onClick={() => void add(false)} data-testid="sv-gallery">
              <Ico name="image" size={24} />
              <span>{t('survey.fromGallery')}</span>
            </button>
          </>
        )}
      </div>
      {big && (
        <Sheet title={props.title} onClose={() => setBig(null)}>
          <PhotoThumb bytes={ctx.assets.bytes.get(big)} alt={props.title} large />
          {!ctx.readOnly && (
            <button
              className="sv-danger sv-block"
              onClick={() => {
                props.onChange(props.photos.filter((p) => p !== big));
                setBig(null);
              }}
            >
              {t('survey.removePhoto')}
            </button>
          )}
        </Sheet>
      )}
    </Section>
  );
}

// ---------- board ----------

const RATINGS = [6, 10, 16, 20, 25, 32, 40];
const SECTIONS = [1.5, 2.5, 4, 6, 10, 16];

function BoardView({ ctx, boardId }: { ctx: Ctx; boardId?: Id }) {
  const { project, t } = ctx;
  const boards = surveyBoards(project);
  const board = (boardId && project.surveyBoards[boardId]) || boards[0];
  if (!board)
    return (
      <div className="sv-pad">
        {!ctx.readOnly && (
          <button
            className="sv-primary sv-block"
            onClick={() =>
              ctx.dispatch(t('survey.addBoard'), [addBoardOp(project, { name: t('survey.mainBoard') })])
            }
            data-testid="sv-add-board"
          >
            + {t('survey.addBoard')}
          </button>
        )}
      </div>
    );
  return <BoardDetail ctx={ctx} board={board} boards={boards} />;
}

function BoardDetail({ ctx, board, boards }: { ctx: Ctx; board: SurveyBoard; boards: SurveyBoard[] }) {
  const { project, t, ai } = ctx;
  const [reading, setReading] = useState<'busy' | { ex: BoardExtraction } | null>(null);
  const [rcdEdit, setRcdEdit] = useState<string | null>(null);
  const patch = (p: Partial<SurveyBoard>) =>
    ctx.dispatch(t('tx.edit'), [{ op: 'update', coll: 'surveyBoards', id: board.id, patch: p }]);
  const circuits = boardCircuits(project, board.id);
  const keys = new Set(board.rcds.map((r) => r.key));
  const groups: Array<{ key?: string; title: string; list: SurveyCircuit[] }> = [
    { title: t('survey.mainBus'), list: circuits.filter((c) => !c.rcdKey || !keys.has(c.rcdKey)) },
    ...board.rcds.map((r) => ({
      key: r.key,
      title: `${t('survey.rcdLabel', { ma: r.sensitivityMa })} · ${r.rcdType} · ${r.ratingA} A`,
      list: circuits.filter((c) => c.rcdKey === r.key),
    })),
  ];
  const read = async () => {
    const id = board.photos.at(-1);
    const bytes = id && ctx.assets.bytes.get(id);
    if (!bytes) return;
    setReading('busy');
    try {
      setReading({ ex: await ai.extractBoard(bytes, 'image/jpeg') });
    } catch (e) {
      setReading(null);
      ctx.notify(
        isAiUnavailable(e) ? t('ai.unavailable') : t('survey.readError', { msg: (e as Error).message }),
      );
    }
  };
  const addCircuit = (rcdKey?: string) => {
    const op = addCircuitOp(project, board.id, {
      ...circuitDefaults('iec.socket'),
      ...(rcdKey ? { rcdKey } : {}),
    });
    ctx.dispatch(t('survey.addCircuit'), [op]);
    ctx.go({ screen: 'project', tab: 'board', circuit: op.value.id });
  };
  const mainRcdMa =
    typeof board.mainRcd?.sensitivityMa === 'number' ? board.mainRcd.sensitivityMa : undefined;
  return (
    <div className="sv-pad" data-testid="sv-board">
      {(boards.length > 1 || !ctx.readOnly) && (
        <div className="sv-board-tabs">
          {boards.map((b) => (
            <button
              key={b.id}
              className={b.id === board.id ? 'on' : ''}
              onClick={() => ctx.go({ screen: 'project', tab: 'board', board: b.id }, true)}
            >
              {b.name || '—'}
            </button>
          ))}
          {!ctx.readOnly && (
            <button
              onClick={() => {
                const op = addBoardOp(project, { name: `${t('survey.mainBoard')} ${boards.length + 1}` });
                ctx.dispatch(t('survey.addBoard'), [op]);
                ctx.go({ screen: 'project', tab: 'board', board: op.value.id }, true);
              }}
              aria-label={t('survey.addBoard')}
              title={t('survey.addBoard')}
            >
              +
            </button>
          )}
        </div>
      )}
      <div className="sv-form">
        <TextField label={t('survey.boardName')} value={board.name} onCommit={(name) => patch({ name })} />
      </div>

      <Photos
        ctx={ctx}
        title={t('survey.boardPhotos')}
        photos={board.photos}
        onChange={(photos) => patch({ photos })}
        extra={
          !ctx.readOnly && (
            <button
              className="sv-secondary"
              disabled={!board.photos.length || reading === 'busy'}
              onClick={() => void ctx.withAccount('board', () => void read())}
              data-testid="sv-read-board"
            >
              {reading === 'busy' ? t('survey.reading') : t('survey.readBoard')}
            </button>
          )
        }
      />

      <Section title={t('survey.mainSwitch')}>
        <Chips
          label={t('survey.mainSwitch')}
          value={typeof board.main?.ratingA === 'number' ? board.main.ratingA : board.main ? 0 : -1}
          options={[
            { value: -1, label: t('survey.none') },
            ...[25, 32, 40, 63].map((v) => ({ value: v, label: `${v} A` })),
          ]}
          onChange={(v) =>
            patch({
              main:
                v === undefined || v < 0
                  ? undefined
                  : { ...(board.main ?? {}), ratingA: v, poles: board.main?.poles ?? 2 },
            })
          }
          testId="sv-main"
        />
      </Section>
      <Section title={t('survey.mainRcd')}>
        <Chips
          label={t('survey.mainRcd')}
          value={mainRcdMa ?? -1}
          options={[
            { value: -1, label: t('survey.none') },
            ...[300, 100, 30].map((v) => ({ value: v, label: `${v} mA` })),
          ]}
          onChange={(v) =>
            patch({
              mainRcd:
                v === undefined || v < 0
                  ? undefined
                  : { ratingA: 40, rcdType: 'A', ...(board.mainRcd ?? {}), sensitivityMa: v },
            })
          }
          testId="sv-main-rcd"
        />
      </Section>

      <Section
        title={t('survey.circuits')}
        end={
          !ctx.readOnly && (
            <button
              className="sv-secondary"
              onClick={() =>
                patch({
                  rcds: [
                    ...board.rcds,
                    { key: nextRcdKey(board), sensitivityMa: 30, rcdType: 'A', ratingA: 40 },
                  ],
                })
              }
              data-testid="sv-add-rcd"
            >
              + {t('survey.addRcd')}
            </button>
          )
        }
      >
        {groups.map((g) => (
          <div className="sv-group" key={g.key ?? 'main'} data-rcd={g.key ?? 'main'}>
            <div className="sv-group-head">
              {g.key ? (
                <button className="sv-link" onClick={() => setRcdEdit(g.key!)}>
                  {g.title}
                </button>
              ) : (
                <span>{g.title}</span>
              )}
            </div>
            {g.list.length > 0 && (
              <div className="sv-card">
                {g.list.map((c) => (
                  <button
                    key={c.id}
                    className={`sv-row${ctx.route.screen === 'project' && ctx.route.circuit === c.id ? ' on' : ''}`}
                    onClick={() => ctx.go({ screen: 'project', tab: 'board', circuit: c.id })}
                    data-code={ctx.codes.get(c.id)}
                  >
                    <span className="sv-code">{ctx.codes.get(c.id)}</span>
                    <span className="sv-row-main">
                      <strong>{c.label || '—'}</strong>
                      <small>{circuitSpecs(ctx, c)}</small>
                    </span>
                    <Ico name="chev" />
                  </button>
                ))}
              </div>
            )}
            {!ctx.readOnly && (
              <button
                className="sv-link sv-add-inline"
                onClick={() => addCircuit(g.key)}
                data-testid="sv-add-circuit"
              >
                + {t('survey.addCircuit')}
              </button>
            )}
          </div>
        ))}
      </Section>

      <Section title={t('survey.earth')}>
        <div className="sv-form">
          <NumberField
            label={t('survey.earthOhm')}
            value={board.earthOhm}
            onCommit={(earthOhm) => {
              const ops = earthResistanceOps(project, earthOhm, board.id);
              if (ops.length) ctx.dispatch(t('tx.edit'), ops);
            }}
            testId="sv-earth"
          />
        </div>
      </Section>
      <Section>
        <TextField
          label={t('survey.notes')}
          value={board.notes}
          multiline
          onCommit={(notes) => patch({ notes })}
        />
      </Section>
      {!ctx.readOnly && (
        <button
          className="sv-danger sv-block"
          onClick={async () => {
            if (
              await ctx.ask({
                title: t('survey.deleteBoard'),
                body: t('survey.deleteBoardConfirm', { name: board.name || '—' }),
                confirm: t('survey.deleteBoard'),
                danger: true,
              })
            ) {
              ctx.go({ screen: 'project', tab: 'board' }, true);
              ctx.dispatch(t('survey.deleteBoard'), removeBoardOps(project, board.id));
            }
          }}
        >
          {t('survey.deleteBoard')}
        </button>
      )}

      {rcdEdit && <RcdSheet ctx={ctx} board={board} rcdKey={rcdEdit} onClose={() => setRcdEdit(null)} />}
      {reading && reading !== 'busy' && (
        <ReadResult
          ctx={ctx}
          ex={reading.ex}
          onClose={() => setReading(null)}
          onApply={() => {
            ctx.dispatch(
              t('survey.readApply'),
              applyBoardSpecOps(project, board.id, extractionToBoard(reading.ex)),
            );
            setReading(null);
          }}
        />
      )}
    </div>
  );
}

function RcdSheet({
  ctx,
  board,
  rcdKey,
  onClose,
}: {
  ctx: Ctx;
  board: SurveyBoard;
  rcdKey: string;
  onClose(): void;
}) {
  const { t } = ctx;
  const rcd = board.rcds.find((r) => r.key === rcdKey);
  if (!rcd) return null;
  const set = (p: Partial<typeof rcd>) =>
    ctx.dispatch(t('tx.edit'), [
      {
        op: 'update',
        coll: 'surveyBoards',
        id: board.id,
        patch: { rcds: board.rcds.map((r) => (r.key === rcdKey ? { ...r, ...p } : r)) },
      },
    ]);
  return (
    <Sheet title={t('survey.rcdLabel', { ma: rcd.sensitivityMa })} onClose={onClose} testId="sv-rcd-sheet">
      <Section title={t('survey.sensitivity')}>
        <Chips
          label={t('survey.sensitivity')}
          value={rcd.sensitivityMa}
          options={[10, 30, 100, 300].map((v) => ({ value: v, label: `${v} mA` }))}
          onChange={(v) => v && set({ sensitivityMa: v })}
        />
      </Section>
      <Section title={t('survey.rcdType')}>
        <Chips
          label={t('survey.rcdType')}
          value={rcd.rcdType}
          options={(['A', 'AC', 'F', 'B'] as const).map((v) => ({ value: v, label: v }))}
          onChange={(v) => v && set({ rcdType: v })}
        />
      </Section>
      <Section title={t('survey.rating')}>
        <Chips
          label={t('survey.rating')}
          value={rcd.ratingA}
          options={[25, 40, 63].map((v) => ({ value: v, label: `${v} A` }))}
          onChange={(v) => v && set({ ratingA: v })}
        />
      </Section>
      <button
        className="sv-danger sv-block"
        onClick={() => {
          ctx.dispatch(t('survey.deleteRcd'), removeRcdOps(ctx.project, board.id, rcdKey));
          onClose();
        }}
      >
        {t('survey.deleteRcd')}
      </button>
      <button className="sv-primary sv-block" onClick={onClose}>
        {t('survey.done')}
      </button>
    </Sheet>
  );
}

function ReadResult({
  ctx,
  ex,
  onClose,
  onApply,
}: {
  ctx: Ctx;
  ex: BoardExtraction;
  onClose(): void;
  onApply(): void;
}) {
  const { t } = ctx;
  const spec = extractionToBoard(ex);
  const circuits = spec.groups.flatMap((g) => g.circuits);
  return (
    <Sheet title={t('survey.readBoard')} onClose={onClose} testId="sv-read-result">
      {ctx.ai.mode === 'demo' && <p className="sv-note">{t('ai.demoBanner')}</p>}
      <p>
        <strong>
          {t('survey.readResult', {
            circuits: circuits.length,
            rcds: spec.groups.filter((g) => g.rcd).length,
          })}
        </strong>
      </p>
      <ul className="sv-read-list">
        {spec.groups.map((g, i) => (
          <li key={i}>
            <strong>
              {g.rcd ? t('survey.rcdLabel', { ma: Number(g.rcd.sensitivityMa ?? 30) }) : t('survey.mainBus')}
            </strong>
            <ul>
              {g.circuits.map((c, j) => (
                <li key={j}>
                  {c.label || '—'} · {String(c.device.props?.curve ?? '')}
                  {String(c.device.props?.ratingA ?? '?')} A
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <p className="muted">{t('survey.readKeeps')}</p>
      <button className="sv-primary sv-block" onClick={onApply} data-testid="sv-read-apply">
        {t('survey.readApply')}
      </button>
    </Sheet>
  );
}

function CircuitView({ ctx, circuit }: { ctx: Ctx; circuit: SurveyCircuit }) {
  const { project, t, locale } = ctx;
  const board = project.surveyBoards[circuit.boardId];
  const patch = (p: Partial<SurveyCircuit>) =>
    ctx.dispatch(t('tx.edit'), [{ op: 'update', coll: 'surveyCircuits', id: circuit.id, patch: p }]);
  const cable = circuit.cable ?? { type: 'XVB', sectionMm2: 0, cores: 3 };
  const setCable = (p: Partial<typeof cable>) => {
    const next = { ...cable, ...p };
    patch({ cable: next.sectionMm2 ? next : undefined });
  };
  const pts = circuitPoints(project, circuit.id);
  const siblings = boardCircuits(project, circuit.boardId).filter(
    (c) => (c.rcdKey ?? '') === (circuit.rcdKey ?? ''),
  );
  return (
    <div className="sv-pad" data-testid="sv-circuit">
      <div className="sv-form">
        <TextField
          label={t('survey.circuitLabel')}
          value={circuit.label}
          placeholder={t('survey.circuitLabelPh')}
          onCommit={(label) => patch({ label })}
          testId="sv-circuit-label"
        />
      </div>
      <Section title={t('survey.device')}>
        <Chips
          label={t('survey.device')}
          value={circuit.device}
          options={[
            { value: 'MCB', label: t('survey.devMCB') },
            { value: 'RCBO', label: t('survey.devRCBO') },
            { value: 'fuse', label: t('survey.devFuse') },
          ]}
          onChange={(device) => device && patch({ device })}
          testId="sv-device"
        />
      </Section>
      <Section title={t('survey.rating')}>
        <Chips
          label={t('survey.rating')}
          value={circuit.ratingA}
          options={RATINGS.map((v) => ({ value: v, label: `${v}` }))}
          onChange={(ratingA) => patch({ ratingA })}
          clearable
          testId="sv-rating"
        />
      </Section>
      {circuit.device !== 'fuse' && (
        <Section title={t('survey.curve')}>
          <Chips
            label={t('survey.curve')}
            value={circuit.curve}
            options={(['B', 'C', 'D'] as const).map((v) => ({ value: v, label: v }))}
            onChange={(curve) => patch({ curve })}
            clearable
          />
        </Section>
      )}
      <Section title={t('survey.section')}>
        <Chips
          label={t('survey.section')}
          value={cable.sectionMm2 || undefined}
          options={SECTIONS.map((v) => ({ value: v, label: fmt(v) }))}
          onChange={(v) => setCable({ sectionMm2: v ?? 0 })}
          clearable
          testId="sv-section"
        />
      </Section>
      <Section title={t('survey.cableType')}>
        <Chips
          label={t('survey.cableType')}
          value={cable.type}
          options={['XVB', 'EXVB', 'VOB', 'XGB'].map((v) => ({ value: v, label: v }))}
          onChange={(type) => type && setCable({ type })}
        />
        <Chips
          label={t('survey.cores')}
          value={cable.cores}
          options={[2, 3, 4, 5].map((v) => ({ value: v, label: `${v}G` }))}
          onChange={(cores) => cores && setCable({ cores })}
        />
        <div className="sv-form">
          <NumberField
            label={t('survey.length')}
            value={cable.lengthM}
            onCommit={(lengthM) => setCable({ lengthM })}
          />
        </div>
      </Section>
      {board && (
        <Section title={t('survey.rcdOf')}>
          <Chips
            label={t('survey.rcdOf')}
            value={circuit.rcdKey ?? ''}
            options={[
              { value: '', label: t('survey.mainBus') },
              ...board.rcds.map((r) => ({ value: r.key, label: `${r.sensitivityMa} mA ${r.rcdType}` })),
            ]}
            onChange={(v) => patch({ rcdKey: v || undefined })}
            testId="sv-circuit-rcd"
          />
        </Section>
      )}
      <Section>
        <label className="sv-check">
          <input
            type="checkbox"
            checked={circuit.legacy}
            onChange={(e) => patch({ legacy: e.target.checked })}
          />
          <span>{t('survey.legacy')}</span>
        </label>
      </Section>
      <Section title={t('survey.circuitPoints')}>
        {pts.length === 0 ? (
          <p className="muted">—</p>
        ) : (
          <div className="sv-card">
            {pts.map((p) => {
              const room = project.surveyRooms[p.roomId];
              return (
                <button
                  key={p.id}
                  className="sv-row"
                  onClick={() => ctx.go({ screen: 'project', tab: 'rooms', room: p.roomId })}
                >
                  <PointIcon symbolId={p.symbolId} size={20} />
                  <span className="sv-row-main">
                    <strong>
                      {p.quantity} × {presetOf(p)?.label[locale]}
                    </strong>
                    <small>{room?.name || '—'}</small>
                  </span>
                  <Ico name="chev" />
                </button>
              );
            })}
          </div>
        )}
      </Section>
      <Section>
        <TextField
          label={t('survey.notes')}
          value={circuit.notes}
          multiline
          onCommit={(notes) => patch({ notes })}
        />
      </Section>
      {!ctx.readOnly && (
        <div className="sv-row-buttons">
          <button
            className="sv-secondary"
            disabled={siblings[0]?.id === circuit.id}
            onClick={() => ctx.dispatch(t('tx.move'), moveOps('surveyCircuits', siblings, circuit.id, -1))}
            aria-label="↑"
          >
            <Ico name="up" />
          </button>
          <button
            className="sv-secondary"
            disabled={siblings.at(-1)?.id === circuit.id}
            onClick={() => ctx.dispatch(t('tx.move'), moveOps('surveyCircuits', siblings, circuit.id, 1))}
            aria-label="↓"
          >
            <Ico name="down" />
          </button>
          <button
            className="sv-danger"
            onClick={async () => {
              if (
                await ctx.ask({
                  title: t('survey.deleteCircuit'),
                  body: t('survey.deleteCircuitConfirm', { code: ctx.codes.get(circuit.id) ?? '' }),
                  confirm: t('survey.deleteCircuit'),
                  danger: true,
                })
              ) {
                ctx.go({ screen: 'project', tab: 'board', board: circuit.boardId }, true);
                ctx.dispatch(t('survey.deleteCircuit'), removeCircuitOps(project, circuit.id));
              }
            }}
          >
            {t('survey.deleteCircuit')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- checks & generation ----------

function ChecksView({ ctx }: { ctx: Ctx }) {
  const { project, t, locale } = ctx;
  const findings = useMemo(() => surveyFindings(project), [project]);
  const sheets = surveySheets(project);
  const stale = surveyStale(project);
  // Measurements refer to the letters and tags of the diagrams the survey gives.
  const previewAnalysis = useMemo(() => {
    const preview = surveyPreview(project);
    return preview ? analyse(preview) : null;
  }, [project]);
  const progress = previewAnalysis ? measurementProgress(project, previewAnalysis) : null;
  const [measuring, setMeasuring] = useState(false);
  const inspection = inspectionsOf(project)[0];
  const remarks = inspection ? remarksFor(project, inspection.id) : [];
  const generate = async () => {
    if (
      sheets.length &&
      !(await ctx.ask({
        title: t('survey.regenerate'),
        body: t('survey.regenerateWarn'),
        confirm: t('survey.regenerate'),
      }))
    )
      return;
    const r = generateFromSurvey(project, LABELS[locale]);
    ctx.dispatch(t('survey.generate'), r.ops);
    ctx.notify(t('survey.generated', { n: r.sheetIds.length }));
  };
  const open = (f: SurveyFinding) => {
    if (!f.target) return;
    if (f.target.kind === 'room') ctx.go({ screen: 'project', tab: 'rooms', room: f.target.id });
    else if (f.target.kind === 'circuit') ctx.go({ screen: 'project', tab: 'board', circuit: f.target.id });
    else ctx.go({ screen: 'project', tab: 'board', board: f.target.id });
  };
  return (
    <div className="sv-pad" data-testid="sv-checks">
      <div className={`sv-gen ${sheets.length ? (stale ? 'stale' : 'ok') : ''}`}>
        <p>{sheets.length ? (stale ? t('survey.stale') : t('survey.upToDate')) : t('survey.notGenerated')}</p>
        <div className="sv-row-buttons">
          {(!sheets.length || stale) && !ctx.readOnly && (
            <button className="sv-primary" onClick={() => void generate()} data-testid="sv-generate">
              {sheets.length ? t('survey.regenerate') : t('survey.generate')}
            </button>
          )}
          {sheets.length > 0 && (
            <>
              <button
                className="sv-secondary"
                onClick={() => ctx.openEditor(sheets[0]!.id)}
                data-testid="sv-open-editor"
              >
                {t('survey.openEditor')}
              </button>
              <button className="sv-secondary" onClick={ctx.exportPdf} data-testid="sv-pdf">
                {t('survey.pdf')}
              </button>
            </>
          )}
        </div>
      </div>
      {progress && (progress.circuits > 0 || progress.rcds > 0) && (
        <Section title={t('measure.title')}>
          <div className={`sv-gen ${progress.failed ? 'stale' : ''}`}>
            <p data-testid="sv-measure-progress">
              {t('measure.progress', {
                m: progress.measured,
                c: progress.circuits,
                r: progress.tested,
                n: progress.rcds,
                f: progress.failed,
              })}
            </p>
            <div className="sv-row-buttons">
              <button className="sv-secondary" onClick={() => setMeasuring(true)} data-testid="sv-measure">
                {t('survey.measure')}
              </button>
            </div>
          </div>
        </Section>
      )}
      {measuring && previewAnalysis && (
        <Sheet title={t('measure.title')} onClose={() => setMeasuring(false)} testId="sv-measure-sheet">
          <MeasurementsEditor
            project={project}
            analysis={previewAnalysis}
            t={t}
            locale={locale}
            readOnly={ctx.readOnly}
            dispatch={ctx.dispatch}
          />
        </Sheet>
      )}
      {remarks.length > 0 && (
        <Section
          title={t('inspection.remarksTitle')}
          end={
            <small data-testid="sv-remarks-count">
              {t('inspection.openCount', {
                n: remarks.filter((r) => r.status === 'open').length,
                total: remarks.length,
              })}
            </small>
          }
        >
          <div className="rm-list">
            {remarks.map((r, i) => (
              <RemarkCard key={r.id} ctx={ctx} remark={r} n={i + 1} />
            ))}
          </div>
        </Section>
      )}
      {findings.length === 0 ? (
        <p className="sv-empty">{t('survey.checksOk')}</p>
      ) : (
        <ul className="sv-findings" data-testid="sv-findings">
          {findings.map((f, i) => (
            <li key={i} className={f.severity} data-rule={f.id}>
              <button onClick={() => open(f)} disabled={!f.target}>
                <span className="sv-sev" aria-hidden="true" />
                <span className="sv-row-main">
                  <strong>{f.message[locale]}</strong>
                  {f.fromDiagram && (
                    <small>
                      {f.id} · {t('survey.fromDiagram', { std: t(`std.${project.meta.standard}`) })}
                    </small>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InfoView({ ctx }: { ctx: Ctx }) {
  const { project, t } = ctx;
  const m = project.meta;
  const set = (patch: Partial<ProjectMeta>) => ctx.dispatch(t('menu.projectInfo'), [{ op: 'meta', patch }]);
  const text = (k: 'title' | 'ownerName' | 'ownerAddress' | 'installationAddress' | 'author' | 'company') => (
    <TextField
      key={k}
      label={t(`meta.${k}` as MessageKey)}
      value={m[k]}
      onCommit={(v) => set({ [k]: v })}
      testId={`sv-meta-${k}`}
    />
  );
  return (
    <div className="sv-pad" data-testid="sv-info">
      <div className="sv-form">
        {text('title')}
        {text('ownerName')}
        {text('installationAddress')}
        {text('ownerAddress')}
        <label className="sv-field">
          <span>{t('meta.date')}</span>
          <input type="date" value={m.date} onChange={(e) => set({ date: e.target.value })} />
        </label>
      </div>
      <Section title={t('meta.standard')}>
        <Chips
          label={t('meta.standard')}
          value={m.standard}
          options={(['rgie', 'nfc15100', 'nen1010'] as const).map((v) => ({
            value: v,
            label: t(`std.label.${v}`),
          }))}
          onChange={(v) => v && set({ standard: v })}
        />
      </Section>
      <Section title={t('meta.earthingSystem')}>
        <Chips
          label={t('meta.earthingSystem')}
          value={m.earthingSystem}
          options={(['TT', 'TN-S', 'TN-C-S', 'IT'] as const).map((v) => ({ value: v, label: v }))}
          onChange={(v) => v && set({ earthingSystem: v })}
        />
      </Section>
      <Section title={t('meta.supplyVoltage')}>
        <Chips
          label={t('meta.supplyVoltage')}
          value={m.supplyVoltage}
          options={['230 V', '3×230 V', '3N×400 V'].map((v) => ({ value: v, label: v }))}
          onChange={(v) => v && set({ supplyVoltage: v })}
        />
      </Section>
      <div className="sv-form">
        <NumberField
          label={t('info.icc')}
          value={m.iccOriginKa}
          onCommit={(iccOriginKa) => set({ iccOriginKa })}
        />
        {text('company')}
        {text('author')}
      </div>
      <p className="muted small">{t('meta.hint')}</p>
      <CloudSection ctx={ctx} />
    </div>
  );
}

function CloudSection({ ctx }: { ctx: Ctx }) {
  const { t, cloudState } = ctx;
  const [busy, setBusy] = useState(false);
  if (!ctx.cloud.client) return null;
  const live = cloudState.live;
  const team = cloudState.me?.teams.find((x) => x.id === (live?.teamId ?? cloudState.team?.id));
  return (
    <Section title={t('survey.cloudSection')}>
      {live ? (
        <p data-testid="sv-in-cloud">
          {t('survey.inCloud', { team: team?.name ?? '' })} · {ctx.status.label}
        </p>
      ) : cloudState.me && cloudState.team ? (
        <button
          className="sv-secondary sv-block"
          disabled={busy || cloudState.offline}
          onClick={async () => {
            setBusy(true);
            try {
              await ctx.upload();
            } finally {
              setBusy(false);
            }
          }}
          data-testid="sv-upload"
        >
          {t('survey.upload', { team: cloudState.team.name })}
        </button>
      ) : (
        <button className="sv-secondary sv-block" onClick={ctx.signIn}>
          {t('survey.signIn')}
        </button>
      )}
    </Section>
  );
}
